import { Dispatch, SetStateAction } from 'react';
import {
  getSavedSearches,
  postSavedSearches,
} from '@doda/common/src/services/getSavedSearchesAPI';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import { AxiosError } from 'axios';
import { moveToServerErrorPage } from '@doda/common/src/logics/moveToServerErrorPage';
import { MAX_SAVED_COUNT } from '@doda/common/src/constants/savedCondition';
import { SAVED_CONDITION_LIMIT } from 'constants/searchCondition';
import { createDataToJpDateFormat } from '@doda/common/src/utils/dateUtil';
import {
  createSearchConditionName,
  makeSendConditionParam,
} from '@doda/common/src/logics/saveCondition/saveCondition';
import { postSearchConditions } from '@doda/common/src/services/conditionRegistAPI';
import { TEMPLATE_CONDITION } from '@doda/common/src/constants/searchConditionModal';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { convertSavedSearchConditionsToQueryParams } from 'logics/selectOverwriteCondition';
import { createSaveConditionsRequest } from 'logics/createSaveConditionsRequest';
import { selectedCurrentConditionCount } from 'logics/selectedCurrentConditionCount';
import { ValidationErrorType } from '../../interface';
import { createSavedConditionParams } from '../createSavedConditionParams';

interface ArgumentType {
  selectedCurrentCondition: SelectedCurrentConditionType;
  isIncomeRangeError: boolean;
  isLogin: boolean;
  overWriteModal: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onToggle: () => void;
  };
  setValidationError: Dispatch<SetStateAction<ValidationErrorType>>;
  setIsShowBallon: Dispatch<SetStateAction<boolean>>;
}

/**
 * 保存するボタン押下時の処理
 * @param {object} param  保存するボタン押下時の処理に必要な引数
 * @param {SelectedCurrentConditionType} param.selectedCurrentCondition 現在設定されている条件
 * @param {boolean} param.isIncomeRangeError 年収バリデーションのフラグ
 * @param {boolean} param.isLogin ログインのフラグ
 * @func overWriteModal 上書きモーダル表示用の関数
 * @func setValidationError バリデーション判定用のセット関数
 * @func setIsShowBallon バルーン表示判定用のセット関数
 */
export const onClickSavedCondition = async ({
  selectedCurrentCondition,
  isIncomeRangeError,
  isLogin,
  overWriteModal,
  setValidationError,
  setIsShowBallon,
}: ArgumentType) => {
  const selectedTotalCount = selectedCurrentConditionCount(
    selectedCurrentCondition
  );
  // バリデーションチェック
  const isErrorNonCondition = !selectedTotalCount;
  const isErrorConditionUpperLimit = selectedTotalCount > SAVED_CONDITION_LIMIT;
  setValidationError({
    isErrorNonCondition,
    isErrorConditionUpperLimit,
  });
  if (isErrorNonCondition || isErrorConditionUpperLimit) {
    window.scrollTo(0, 0);
    return;
  }
  // 希望年収エラーがあるか判定
  if (isIncomeRangeError) return;

  if (isLogin) {
    // ログイン状態の場合
    try {
      const savedCount = (await getSavedSearches()).length;
      // すでに保存した検索条件の総数が5件ある場合
      if (savedCount >= MAX_SAVED_COUNT) {
        overWriteModal.onOpen();
        return;
      }

      // 5件未満の場合、保存処理
      const conditionName = `${createDataToJpDateFormat()}の検索条件${
        savedCount + 1
      }`;
      const reqParams = createSavedConditionParams(
        selectedCurrentCondition,
        conditionName
      );
      await postSavedSearches(reqParams);
      setIsShowBallon(true);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 500) {
        moveToServerErrorPage(
          `検索条件保存失敗でシステムエラー画面表示、message:${axiosError.message}`
        );
        return;
      }
      if (axiosError.response?.status === 401) {
        const param = encodeURIComponent(DODA_SITE_URL.jobSearchTop);
        window.location.href = `${DODA_SITE_URL.loginScreen}?url=${param}`;
        return;
      }
      moveToServerErrorPage(
        `検索条件保存失敗でシステムエラー画面表示、message:${axiosError.message}`
      );
    }
  } else {
    // TODO: サイドバー処理が同じなので、共通化させる
    // 未ログイン状態の場合
    const conditions = createSaveConditionsRequest(selectedCurrentCondition);
    const searchConditionName = createSearchConditionName();
    const url = '/dcfront/login/login/';
    const param = {
      oldestDayWdtno: 0,
      SP_CC_wsernm: searchConditionName,
    };
    // 検索条件保存リクエストパラメータから対応するクエリパラメータに変換
    const newConditions = convertSavedSearchConditionsToQueryParams(conditions);
    const sendCondition = makeSendConditionParam(newConditions);
    const newParam = {
      ...param,
      ...sendCondition,
      ...TEMPLATE_CONDITION,
    };
    postSearchConditions(url, newParam, {});
  }
};
